/**
 * Power Law Distribution Utilities
 * Mathematical implementation of Power Law distributions for extreme events modeling
 * 
 * Power Law Formula: P(x) = k × x^(-α)
 * Where:
 * - k: Scaling coefficient (specific to market/context)
 * - α: Power law exponent (typically 2-3 for extreme events)
 * - x: Impact magnitude (as multiple of base case)
 */

import { logger } from './logger';

/**
 * Power Law Distribution Class
 * Implements P(x) = k × x^(-α) for extreme event probability modeling
 */
export class PowerLawDistribution {
    constructor(k, alpha, xMin = 1, xMax = 10) {
        this.k = k;
        this.alpha = alpha;
        this.xMin = xMin;
        this.xMax = xMax;
        
        // Validate parameters
        if (k <= 0 || alpha <= 0) {
            throw new Error('Power law parameters k and alpha must be positive');
        }
        if (xMin >= xMax) {
            throw new Error('xMin must be less than xMax');
        }
        
        this.normalizationConstant = this.calculateNormalizationConstant();
        
        logger.debug('Power law distribution created', { k, alpha, xMin, xMax });
    }
    
    /**
     * Calculate normalization constant for proper probability distribution
     * Ensures ∫P(x)dx = 1 over the range [xMin, xMax]
     */
    calculateNormalizationConstant() {
        if (this.alpha === 1) {
            return this.k / Math.log(this.xMax / this.xMin);
        } else {
            const term1 = Math.pow(this.xMin, 1 - this.alpha);
            const term2 = Math.pow(this.xMax, 1 - this.alpha);
            return this.k * (this.alpha - 1) / (term1 - term2);
        }
    }
    
    /**
     * Calculate probability density function P(x) = k × x^(-α)
     * @param {number} x - Impact magnitude
     * @returns {number} Probability density
     */
    pdf(x) {
        if (x < this.xMin || x > this.xMax) {
            return 0;
        }
        return this.normalizationConstant * Math.pow(x, -this.alpha);
    }
    
    /**
     * Calculate cumulative distribution function
     * @param {number} x - Impact magnitude
     * @returns {number} Cumulative probability
     */
    cdf(x) {
        if (x <= this.xMin) {
            return 0;
        }
        if (x >= this.xMax) {
            return 1;
        }
        
        if (this.alpha === 1) {
            return Math.log(x / this.xMin) / Math.log(this.xMax / this.xMin);
        } else {
            const numerator = Math.pow(this.xMin, 1 - this.alpha) - Math.pow(x, 1 - this.alpha);
            const denominator = Math.pow(this.xMin, 1 - this.alpha) - Math.pow(this.xMax, 1 - this.alpha);
            return numerator / denominator;
        }
    }
    
    /**
     * Generate random sample from power law distribution
     * Uses inverse transform sampling method
     * @returns {number} Random sample
     */
    sample() {
        const u = Math.random();
        return this.quantile(u);
    }
    
    /**
     * Calculate quantile function (inverse CDF)
     * @param {number} p - Probability [0,1]
     * @returns {number} Quantile value
     */
    quantile(p) {
        if (p < 0 || p > 1) {
            throw new Error('Probability must be between 0 and 1');
        }
        
        if (p === 0) return this.xMin;
        if (p === 1) return this.xMax;
        
        if (this.alpha === 1) {
            return this.xMin * Math.exp(p * Math.log(this.xMax / this.xMin));
        } else {
            const term1 = Math.pow(this.xMin, 1 - this.alpha);
            const term2 = Math.pow(this.xMax, 1 - this.alpha);
            const value = term1 - p * (term1 - term2);
            return Math.pow(value, 1 / (1 - this.alpha));
        }
    }
    
    /**
     * Calculate statistical moments
     * @returns {object} Statistical properties
     */
    getStatistics() {
        const mean = this.calculateMean();
        const variance = this.calculateVariance();
        
        return {
            mean,
            variance,
            standardDeviation: Math.sqrt(variance),
            skewness: this.calculateSkewness(),
            kurtosis: this.calculateKurtosis(),
            mode: this.xMin, // Mode is always at xMin for power law
            median: this.quantile(0.5)
        };
    }
    
    /**
     * Calculate mean of power law distribution
     */
    calculateMean() {
        if (this.alpha <= 2) {
            return Infinity; // Mean doesn't exist for α ≤ 2
        }
        
        const term1 = this.alpha - 1;
        const term2 = this.alpha - 2;
        const term3 = Math.pow(this.xMin, 2 - this.alpha) - Math.pow(this.xMax, 2 - this.alpha);
        const term4 = Math.pow(this.xMin, 1 - this.alpha) - Math.pow(this.xMax, 1 - this.alpha);
        
        return (term1 / term2) * (term3 / term4);
    }
    
    /**
     * Calculate variance of power law distribution
     */
    calculateVariance() {
        if (this.alpha <= 3) {
            return Infinity; // Variance doesn't exist for α ≤ 3
        }
        
        const mean = this.calculateMean();
        const secondMoment = this.calculateSecondMoment();
        
        return secondMoment - mean * mean;
    }
    
    /**
     * Calculate second moment E[X²]
     */
    calculateSecondMoment() {
        if (this.alpha <= 3) {
            return Infinity;
        }
        
        const term1 = this.alpha - 1;
        const term2 = this.alpha - 3;
        const term3 = Math.pow(this.xMin, 3 - this.alpha) - Math.pow(this.xMax, 3 - this.alpha);
        const term4 = Math.pow(this.xMin, 1 - this.alpha) - Math.pow(this.xMax, 1 - this.alpha);
        
        return (term1 / term2) * (term3 / term4);
    }
    
    /**
     * Calculate skewness (simplified approximation)
     */
    calculateSkewness() {
        if (this.alpha <= 4) {
            return Infinity;
        }
        
        // Simplified approximation for finite domain power law
        return 2 * Math.sqrt(this.alpha - 3) / (this.alpha - 4);
    }
    
    /**
     * Calculate kurtosis (simplified approximation)
     */
    calculateKurtosis() {
        if (this.alpha <= 5) {
            return Infinity;
        }
        
        // Simplified approximation
        const numerator = 6 * (this.alpha * this.alpha - this.alpha - 6);
        const denominator = (this.alpha - 4) * (this.alpha - 5);
        return 3 + numerator / denominator;
    }
}

/**
 * Extreme Event Generator
 * Generates extreme events based on power law probability and impact
 */
export class ExtremeEventGenerator {
    constructor(events, powerLawParams) {
        this.events = events;
        this.powerLawParams = powerLawParams; // { k, alpha }
        this.distribution = new PowerLawDistribution(
            powerLawParams.k,
            powerLawParams.alpha,
            0.1, // xMin: minimum impact (10% of base case)
            1.0  // xMax: maximum impact (total loss)
        );
        
        logger.debug('Extreme event generator created', { 
            eventCount: events.length, 
            powerLawParams 
        });
    }
    
    /**
     * Sample an extreme event for a given scenario
     * @returns {object|null} Extreme event or null if no event occurs
     */
    sampleEvent() {
        // First, determine if any extreme event occurs based on power law
        const eventOccurs = Math.random() < this.powerLawParams.k;
        
        if (!eventOccurs) {
            return null;
        }
        
        // Select a random enabled event
        const enabledEvents = this.events.filter(event => event.enabled);
        if (enabledEvents.length === 0) {
            return null;
        }
        
        const selectedEvent = enabledEvents[Math.floor(Math.random() * enabledEvents.length)];
        
        // Generate impact magnitude using power law
        const impactMagnitude = this.distribution.sample();
        
        // Convert to impact multiplier within event's range
        const [minImpact, maxImpact] = selectedEvent.impactRange;
        const impactMultiplier = minImpact + (maxImpact - minImpact) * (1 - impactMagnitude);
        
        return {
            event: selectedEvent,
            impactMultiplier,
            impactMagnitude,
            probability: selectedEvent.probability,
            category: selectedEvent.category
        };
    }
    
    /**
     * Generate multiple extreme events for a simulation
     * @param {number} scenarios - Number of scenarios to generate
     * @returns {Array} Array of extreme events (or null for no event)
     */
    generateEvents(scenarios) {
        const events = [];
        
        for (let i = 0; i < scenarios; i++) {
            events.push(this.sampleEvent());
        }
        
        logger.debug(`Generated ${scenarios} extreme event scenarios`, {
            eventsOccurred: events.filter(e => e !== null).length,
            eventRate: events.filter(e => e !== null).length / scenarios
        });
        
        return events;
    }
    
    /**
     * Calculate expected annual loss from extreme events
     * @returns {number} Expected annual loss multiplier
     */
    calculateExpectedAnnualLoss() {
        let expectedLoss = 0;
        
        this.events.forEach(event => {
            if (event.enabled) {
                const averageImpact = (event.impactRange[0] + event.impactRange[1]) / 2;
                expectedLoss += event.probability * (1 - averageImpact);
            }
        });
        
        return expectedLoss;
    }
    
    /**
     * Get extreme event statistics
     * @returns {object} Statistics about extreme events
     */
    getEventStatistics() {
        const enabledEvents = this.events.filter(event => event.enabled);
        
        const totalAnnualProbability = enabledEvents.reduce((sum, event) => sum + event.probability, 0);
        const averageImpact = enabledEvents.reduce((sum, event) => {
            const avgImpact = (event.impactRange[0] + event.impactRange[1]) / 2;
            return sum + avgImpact;
        }, 0) / enabledEvents.length;
        
        const severityDistribution = enabledEvents.reduce((dist, event) => {
            const avgImpact = (event.impactRange[0] + event.impactRange[1]) / 2;
            if (avgImpact < 0.3) dist.catastrophic++;
            else if (avgImpact < 0.6) dist.severe++;
            else if (avgImpact < 0.9) dist.moderate++;
            else dist.minor++;
            return dist;
        }, { catastrophic: 0, severe: 0, moderate: 0, minor: 0 });
        
        return {
            enabledEvents: enabledEvents.length,
            totalEvents: this.events.length,
            totalAnnualProbability,
            averageImpact,
            expectedAnnualLoss: this.calculateExpectedAnnualLoss(),
            severityDistribution,
            powerLawParams: this.powerLawParams,
            distributionStats: this.distribution.getStatistics()
        };
    }
}

/**
 * Utility functions for power law analysis
 */

/**
 * Fit power law parameters to historical data
 * @param {Array<number>} data - Historical loss data
 * @returns {object} Fitted parameters { k, alpha }
 */
export function fitPowerLaw(data) {
    if (data.length < 10) {
        logger.warn('Insufficient data for power law fitting', { dataPoints: data.length });
        return { k: 0.1, alpha: 2.0 }; // Default values
    }
    
    // Sort data in descending order
    const sortedData = data.sort((a, b) => b - a);
    
    // Use rank-frequency approach for parameter estimation
    const n = sortedData.length;
    let sumLogX = 0;
    let sumLogRank = 0;
    
    for (let i = 0; i < n; i++) {
        if (sortedData[i] > 0) {
            sumLogX += Math.log(sortedData[i]);
            sumLogRank += Math.log(i + 1);
        }
    }
    
    // Estimate alpha using linear regression on log-log plot
    const alpha = -sumLogRank / sumLogX;
    
    // Estimate k from normalization
    const k = 1 / (n * Math.pow(sortedData[0], -alpha));
    
    logger.info('Power law parameters fitted', { k, alpha, dataPoints: n });
    
    return { k: Math.abs(k), alpha: Math.abs(alpha) };
}

/**
 * Test if data follows power law distribution
 * @param {Array<number>} data - Data to test
 * @returns {object} Test results
 */
export function testPowerLaw(data) {
    const { k, alpha } = fitPowerLaw(data);
    const distribution = new PowerLawDistribution(k, alpha);
    
    // Kolmogorov-Smirnov test (simplified)
    const n = data.length;
    const sortedData = data.sort((a, b) => a - b);
    
    let maxDifference = 0;
    for (let i = 0; i < n; i++) {
        const empiricalCDF = (i + 1) / n;
        const theoreticalCDF = distribution.cdf(sortedData[i]);
        const difference = Math.abs(empiricalCDF - theoreticalCDF);
        maxDifference = Math.max(maxDifference, difference);
    }
    
    // Critical value for KS test (approximate)
    const criticalValue = 1.36 / Math.sqrt(n);
    const isPowerLaw = maxDifference < criticalValue;
    
    return {
        isPowerLaw,
        maxDifference,
        criticalValue,
        pValue: Math.exp(-2 * n * maxDifference * maxDifference),
        fittedParams: { k, alpha }
    };
}

/**
 * Generate synthetic power law data for testing
 * @param {number} n - Number of samples
 * @param {number} k - Scaling parameter
 * @param {number} alpha - Power law exponent
 * @returns {Array<number>} Synthetic data
 */
export function generateSyntheticPowerLawData(n, k, alpha) {
    const distribution = new PowerLawDistribution(k, alpha);
    const data = [];
    
    for (let i = 0; i < n; i++) {
        data.push(distribution.sample());
    }
    
    return data;
}

/**
 * Calculate Value at Risk (VaR) for power law distribution
 * @param {PowerLawDistribution} distribution - Power law distribution
 * @param {number} confidenceLevel - Confidence level (e.g., 0.95)
 * @returns {number} VaR value
 */
export function calculateVaR(distribution, confidenceLevel = 0.95) {
    return distribution.quantile(1 - confidenceLevel);
}

/**
 * Calculate Expected Shortfall (Conditional VaR)
 * @param {PowerLawDistribution} distribution - Power law distribution
 * @param {number} confidenceLevel - Confidence level (e.g., 0.95)
 * @returns {number} Expected shortfall
 */
export function calculateExpectedShortfall(distribution, confidenceLevel = 0.95) {
    const var_ = calculateVaR(distribution, confidenceLevel);
    
    // Numerical integration for expected shortfall
    const steps = 1000;
    const dx = (distribution.xMax - var_) / steps;
    let sum = 0;
    
    for (let i = 0; i < steps; i++) {
        const x = var_ + i * dx;
        sum += x * distribution.pdf(x) * dx;
    }
    
    const tailProbability = 1 - distribution.cdf(var_);
    return sum / tailProbability;
}

export default {
    PowerLawDistribution,
    ExtremeEventGenerator,
    fitPowerLaw,
    testPowerLaw,
    generateSyntheticPowerLawData,
    calculateVaR,
    calculateExpectedShortfall
};